/**
 * حذفِ حساب — قرینه‌ی apps/mobile/app/delete-account.tsx.
 * صفحه‌ی جدا و تاییدِ چندمرحله‌ای عمدی است: این کار برگشت ندارد.
 *
 * ═══ چرا این یکی صفحه دو حالته است (WHOSUP-169) ═══
 *
 * این همان آدرسی است که در کنسولِ گوگل‌پلی به‌عنوانِ «Delete app account»
 * ثبت شده، و شرطِ گوگل این است که **بدونِ ورود** دیده شود. تا امروز پشتِ
 * `<Private>` بود، پس بازبینِ گوگل به /auth پرت می‌شد و فرمِ ورود می‌دید نه
 * توضیحِ حذف.
 *
 * پس گارد از مسیر برداشته شد و به داخلِ خودِ صفحه آمد: مهمان **توضیح**
 * می‌بیند و کاربرِ واردشده ابزارِ حذف. هیچ عملی بدونِ نشست انجام نمی‌شود؛
 * خودِ `delete_my_account` هم روی `auth.uid()` می‌ایستد.
 *
 * این حالتِ عمومی عمداً فقط وب است: موضوعش «یک آدرسِ عمومی روی وب» است و
 * روی موبایل معنایی ندارد.
 *
 * ═══ و چرا برای حذف باید دوباره وارد شد ═══
 *
 * حذفِ حساب برگشت ندارد و صفحه‌اش حالا **عمومی** است، یعنی یک دستگاهِ
 * قفل‌نشده‌ی رهاشده تا این دکمه فقط چند لمس فاصله دارد. پس پیش از قدمِ
 * آخر یک‌بار دیگر پرسیده می‌شود «خودتی؟» — با همان کاری که هر کسی بلد
 * است: خروج، و ورودِ دوباره. مکانیزمش در `core/lib/afterAuth` است، همان‌جا
 * که نوشته چه چیزی را ثابت می‌کند و چه چیزی را نه.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { profileRepo } from '@/core/data/socialRepo';
import { useSession } from '@/core/session';
import {
  armDeleteReauth, clearDeleteReauth, rememberAfterAuth, takeDeleteReauth,
} from '@/core/lib/afterAuth';
import { CONTACT_EMAIL } from '@/core/legal/terms';
import { t, useLocale } from '@/core/lib/i18n';
import { reportError } from '@/core/lib/report';
import { Note } from '@/ui/kit';
import { EraseDataSection } from './EraseDataSection';

/** آدرسِ خودِ همین صفحه — سه‌جا لازم می‌شود، پس یک‌بار نوشته شده. */
const HERE = '/settings/delete-account';

/**
 * همان سه بندی که هر دو حالت نشان می‌دهند — و همان سه بندی که برای بازبینِ
 * گوگل کلِ صفحه است. بیرون کشیده شده تا مهمان و کاربرِ واردشده نتوانند
 * تصادفاً دو متنِ متفاوت ببینند.
 */
function WhatHappens() {
  return (
    <>
      <Note tone="warn">{t('deleteAccountWarn')}</Note>
      <p className="dim">{t('deleteAccountGone')}</p>
      <p className="dim">{t('deleteAccountHashed')}</p>
      <p className="dim">{t('deleteAccountStays')}</p>
    </>
  );
}

export function DeleteAccountPage() {
  useLocale();
  const { session, profile, loading, signOut, signOutAfterDelete } = useSession();
  const qc = useQueryClient();
  const nav = useNavigate();
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  /** از خروج/ورودِ همین حالا برگشته — قدمِ آخر باز است. */
  const [reauthed, setReauthed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * نشانِ «دوباره وارد شد» فقط وقتی خوانده می‌شود که نشستی هم باشد.
   *
   * وگرنه مهمانی که وسطِ راه دکمه‌ی back را زده، نشانِ خودش را مصرف می‌کرد و
   * بعد از ورود دوباره از اول شروع می‌کرد.
   */
  useEffect(() => {
    if (!session) return;
    let alive = true;
    void takeDeleteReauth().then((r) => {
      if (!alive || !r.ok) return;
      setReauthed(true);
      // دلیلی که قبلِ خروج نوشته بود، دوباره سرِ جایش — یک‌بار نوشتنش بس است.
      if (r.reason) setReason(r.reason);
    });
    return () => { alive = false; };
  }, [session]);

  const remove = useMutation({
    mutationFn: () => profileRepo.deleteMine(profile!.id, reason.trim()),
    onSuccess: async () => {
      // ترتیب مهمه: اول کشِ محلی خالی شه، بعد خروج.
      qc.clear();
      // و رفتن به /auth شرطی نیست — قرینه‌ی موبایل، به همان دلیل.
      try {
        await signOutAfterDelete();
      } finally {
        nav('/auth', { replace: true });
      }
    },
    onError: (e) => { reportError('web.account.delete', e); setError(t('errorRetry')); },
  });

  /**
   * قدمِ تایید: خروج، و برگشت به همین صفحه بعد از ورود.
   *
   * `rememberAfterAuth` جدا از `?from=` لازم است: ورود با گوگل/اپل از سایت
   * بیرون می‌رود و روی `/` فرود می‌آید، جایی که هیچ `?from=`ای نمانده.
   */
  const reauth = async () => {
    setConfirming(false);
    setError(null);
    await armDeleteReauth(reason.trim());
    await rememberAfterAuth(HERE);
    try {
      await signOut();
    } finally {
      nav(`/auth?from=${encodeURIComponent(HERE)}`, { replace: true });
    }
  };

  /** «نه، حسابم بماند» — نشانِ نیمه‌کاره نباید برای دفعه‌ی بعد بماند. */
  const keep = () => {
    setConfirming(false);
    setReauthed(false);
    void clearDeleteReauth();
  };

  /**
   * `loading` یعنی نشست هنوز از localStorage خوانده نشده. بدونِ این شرط،
   * کاربرِ واردشده در همان تیکِ اول یک پلک «برای حذف وارد شو» می‌بیند —
   * همان دلیلی که `Private` هم این شرط را دارد.
   */
  const guest = !loading && !session;

  return (
    <div className="feed">
      <h1 className="feed-title">{t('deleteAccount')}</h1>
      <p className="dim">{t('deleteAccountSub')}</p>

      <WhatHappens />

      {guest ? (
        <>
          <p className="dim">{t('deleteAccountSignInFirst')}</p>
          {/* `?from=` و نه `state`: این لینک باید با کپی/پیست هم کار کند —
              نگاه کن به `AuthRoute` در app/routes.tsx. */}
          <Link className="btn fill block" data-testid="delete-account-signin"
            to={`/auth?from=${encodeURIComponent(HERE)}`}>
            {t('deleteAccountSignInCta')}
          </Link>
          {/* کسی که دیگر اصلاً به حسابش دسترسی ندارد هم باید راهی داشته
              باشد — وگرنه «حذفِ حساب» فقط برای کسی است که می‌تواند وارد شود. */}
          <p className="dim">{t('deleteAccountLockedOut', { email: CONTACT_EMAIL })}</p>
        </>
      ) : null}

      {session ? (
        <>
          {/* بالای بخشِ حذفِ حساب: راهِ کم‌هزینه‌تر باید اول دیده شود. */}
          <EraseDataSection />

          <h3>{t('deleteAccountReason')}</h3>
          <textarea className="input textarea" placeholder={t('deleteAccountReasonPlaceholder')}
            value={reason} onChange={(e) => setReason(e.target.value)} rows={4} />
          <p className="dim">{t('deleteAccountReasonHint')}</p>

          {error ? <p className="error">{error}</p> : null}

          {reauthed ? (
            /* قدمِ آخر — و عمداً نه داخلِ یک مودال: کاربر همین حالا از یک
               خروج/ورود برگشته و باید ببیند چرا برگشته و کجاست. */
            <Note tone="warn">
              <b>{t('deleteAccountReauthDone')}</b>
            </Note>
          ) : null}

          {reauthed ? (
            <>
              {/* `!profile` مثلِ موبایل: درست بعد از ورودِ دوباره، پروفایل هنوز
                  نرسیده و کلیکِ زودهنگام به `profile!.id` می‌خورد. */}
              <button type="button" className="btn danger-solid block"
                disabled={!profile || remove.isPending}
                data-testid="delete-account-final" onClick={() => remove.mutate()}>
                {remove.isPending ? t('deleteAccountWorking') : t('deleteAccountConfirmYes')}
              </button>
              <button type="button" className="btn ghost block" onClick={keep}>
                {t('deleteAccountKeep')}
              </button>
            </>
          ) : (
            <button type="button" className="btn" disabled={!profile || remove.isPending}
              data-testid="delete-account-start" onClick={() => setConfirming(true)}>
              {t('deleteAccount')}
            </button>
          )}

          {confirming ? (
            <div className="modal-backdrop" onClick={() => setConfirming(false)}>
              <div className="card modal" onClick={(e) => e.stopPropagation()}>
                <h3>{t('deleteAccountReauthTitle')}</h3>
                <p className="dim">{t('deleteAccountWarn')}</p>
                <p className="dim">{t('deleteAccountReauthBody')}</p>
                <button type="button" className="btn danger-solid block"
                  data-testid="delete-account-reauth" onClick={() => void reauth()}>
                  {t('deleteAccountReauthGo')}
                </button>
                <button type="button" className="btn ghost block" onClick={keep}>
                  {t('deleteAccountKeep')}
                </button>
              </div>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
